import { useState, useRef, useCallback } from 'react'
import { Upload, Progress, Card, Space, Typography, Button, message, Image } from 'antd'
import {
  CloudUploadOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons'
import type { UploadProps } from 'antd'

const { Text, Title } = Typography
const { Dragger } = Upload

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

interface MediaInfo {
  file: File
  url: string
  thumbnailUrl: string
  type: 'video' | 'image'
  duration?: number // Only for video
  width: number
  height: number
  size: number
}

interface MediaUploaderProps {
  value?: string // S3 key of uploaded media
  onChange?: (value: string | undefined) => void
  onMediaInfo?: (info: MediaInfo | null) => void
  maxVideoDuration?: number // seconds
  maxSize?: number // bytes
  acceptImages?: boolean
  acceptVideos?: boolean
}

// Format seconds to mm:ss
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Format bytes to human readable
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function MediaUploader({
  value,
  onChange,
  onMediaInfo,
  maxVideoDuration = 180, // 3 minutes default
  maxSize = 500 * 1024 * 1024, // 500MB default
  acceptImages = true,
  acceptVideos = true,
}: MediaUploaderProps) {
  const [mediaInfo, setMediaInfo] = useState<MediaInfo | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Generate thumbnail from video
  const generateVideoThumbnail = useCallback((video: HTMLVideoElement): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', 0.8))
      } else {
        resolve('')
      }
    })
  }, [])

  // Extract video metadata
  const extractVideoInfo = useCallback(
    async (file: File): Promise<MediaInfo | null> => {
      return new Promise((resolve) => {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true

        video.onloadedmetadata = async () => {
          video.currentTime = Math.min(1, video.duration)
        }

        video.onseeked = async () => {
          const thumbnail = await generateVideoThumbnail(video)

          const info: MediaInfo = {
            file,
            url: URL.createObjectURL(file),
            thumbnailUrl: thumbnail,
            type: 'video',
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight,
            size: file.size,
          }

          URL.revokeObjectURL(video.src)
          resolve(info)
        }

        video.onerror = () => {
          resolve(null)
        }

        video.src = URL.createObjectURL(file)
      })
    },
    [generateVideoThumbnail]
  )

  // Extract image metadata
  const extractImageInfo = useCallback(async (file: File): Promise<MediaInfo | null> => {
    return new Promise((resolve) => {
      const img = new window.Image()

      img.onload = () => {
        const info: MediaInfo = {
          file,
          url: URL.createObjectURL(file),
          thumbnailUrl: URL.createObjectURL(file), // Use same URL for thumbnail
          type: 'image',
          width: img.naturalWidth,
          height: img.naturalHeight,
          size: file.size,
        }
        resolve(info)
      }

      img.onerror = () => {
        resolve(null)
      }

      img.src = URL.createObjectURL(file)
    })
  }, [])

  // Upload media to S3
  const uploadToS3 = async (file: File): Promise<string | null> => {
    try {
      const token = localStorage.getItem('admin_access_token')

      // Get presigned URL
      const urlResponse = await fetch(`${API_URL}/admin/social-media/media/upload-url`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          folder: 'social-media',
        }),
      })

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL')
      }

      const { uploadUrl, key } = await urlResponse.json()

      // Upload with progress tracking using XMLHttpRequest
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            setUploadProgress(percent)
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(key)
          } else {
            reject(new Error('Upload failed'))
          }
        }

        xhr.onerror = () => reject(new Error('Upload failed'))

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })
    } catch (error) {
      console.error('Upload error:', error)
      return null
    }
  }

  // Handle file selection
  const handleFile = async (file: File) => {
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')

    // Validate file type
    if (!isImage && !isVideo) {
      message.error('Izaberite sliku ili video fajl')
      return false
    }

    if (isImage && !acceptImages) {
      message.error('Slike nisu dozvoljene')
      return false
    }

    if (isVideo && !acceptVideos) {
      message.error('Video fajlovi nisu dozvoljeni')
      return false
    }

    // Validate file size
    if (file.size > maxSize) {
      message.error(`Fajl je prevelik. Maksimum je ${formatFileSize(maxSize)}`)
      return false
    }

    // Extract media info
    let info: MediaInfo | null = null
    if (isVideo) {
      info = await extractVideoInfo(file)
      if (info && info.duration && info.duration > maxVideoDuration) {
        message.error(`Video je predugacak. Maksimum je ${formatDuration(maxVideoDuration)}`)
        return false
      }
    } else {
      info = await extractImageInfo(file)
    }

    if (!info) {
      message.error('Neispravan fajl')
      return false
    }

    setMediaInfo(info)
    onMediaInfo?.(info)

    // Upload to S3
    setUploading(true)
    setUploadProgress(0)

    const key = await uploadToS3(file)

    setUploading(false)

    if (key) {
      onChange?.(key)
      message.success(isVideo ? 'Video je uspesno otpremljen' : 'Slika je uspesno otpremljena')
    } else {
      message.error('Otpremanje nije uspelo')
      handleRemove()
    }

    return false // Prevent default upload behavior
  }

  // Handle remove
  const handleRemove = () => {
    if (mediaInfo?.url) {
      URL.revokeObjectURL(mediaInfo.url)
    }
    setMediaInfo(null)
    setUploadProgress(0)
    onChange?.(undefined)
    onMediaInfo?.(null)
  }

  // Toggle play/pause for video
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  // Build accept string
  const acceptTypes: string[] = []
  if (acceptImages) acceptTypes.push('image/jpeg', 'image/png', 'image/gif', 'image/webp')
  if (acceptVideos) acceptTypes.push('video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo')

  const uploadProps: UploadProps = {
    name: 'media',
    multiple: false,
    accept: acceptTypes.join(','),
    showUploadList: false,
    beforeUpload: handleFile,
  }

  // If we have media, show preview
  if (mediaInfo) {
    return (
      <Card
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden', borderRadius: 12 }}
      >
        {/* Media Preview */}
        <div
          style={{
            position: 'relative',
            backgroundColor: '#000',
            aspectRatio: mediaInfo.type === 'video' ? '9/16' : undefined,
            maxHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {mediaInfo.type === 'video' ? (
            <>
              <video
                ref={videoRef}
                src={mediaInfo.url}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
                onEnded={() => setIsPlaying(false)}
                onClick={togglePlay}
              />

              {/* Play overlay */}
              {!isPlaying && (
                <div
                  onClick={togglePlay}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    background: 'rgba(0,0,0,0.3)',
                  }}
                >
                  <PlayCircleOutlined style={{ fontSize: 64, color: 'white' }} />
                </div>
              )}
            </>
          ) : (
            <Image
              src={mediaInfo.url}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: 400,
                objectFit: 'contain',
              }}
              preview={false}
            />
          )}

          {/* Upload progress overlay */}
          {uploading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.7)',
              }}
            >
              <Progress
                type="circle"
                percent={uploadProgress}
                strokeColor="#1890ff"
                trailColor="rgba(255,255,255,0.2)"
              />
              <Text style={{ color: 'white', marginTop: 16 }}>Otpremanje...</Text>
            </div>
          )}
        </div>

        {/* Media Info */}
        <div style={{ padding: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
              {mediaInfo.type === 'video' ? (
                <Text type="secondary">
                  <VideoCameraOutlined /> {formatDuration(mediaInfo.duration || 0)}
                </Text>
              ) : (
                <Text type="secondary">
                  <PictureOutlined /> Slika
                </Text>
              )}
              <Text type="secondary">
                {mediaInfo.width}x{mediaInfo.height}
              </Text>
              <Text type="secondary">{formatFileSize(mediaInfo.size)}</Text>
            </Space>

            <Text ellipsis style={{ maxWidth: '100%' }}>
              {mediaInfo.file.name}
            </Text>

            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              disabled={uploading}
              block
            >
              Ukloni {mediaInfo.type === 'video' ? 'video' : 'sliku'}
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // Empty state - show upload zone
  return (
    <Dragger {...uploadProps} style={{ borderRadius: 12 }}>
      <div style={{ padding: '40px 20px' }}>
        <p className="ant-upload-drag-icon">
          <CloudUploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <Title level={5} style={{ margin: '16px 0 8px' }}>
          Prevuci fajl ovde
        </Title>
        <Text type="secondary">ili klikni za izbor fajla</Text>
        <div style={{ marginTop: 16 }}>
          <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
            {acceptImages && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PictureOutlined /> JPG, PNG, GIF
              </Text>
            )}
            {acceptVideos && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <VideoCameraOutlined /> MP4, MOV, WebM
              </Text>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Max {formatFileSize(maxSize)}
            </Text>
          </Space>
        </div>
      </div>
    </Dragger>
  )
}

// Keep VideoUploader as an alias for backwards compatibility
export const VideoUploader = MediaUploader

export default MediaUploader
